import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, Sparkles, ListChecks, ChevronDown, ChevronRight, ArrowLeft, Search } from "lucide-react";
import { cn } from "../lib/utils";
import TrainingSessions from "../components/TrainingSessions";
import CoachDrillBankSection from "../components/CoachDrillBankSection";
import { useAuth } from "../context/AuthContext";
import { fetchAssignedGuides, fetchPublishedGuides } from "../lib/guides";
import { fetchAssignedDrills } from "../lib/drills";
import { markSurfaceNotificationsRead } from "../lib/notificationsRead";
import LoadFailureNote from "../components/LoadFailureNote";
import { coachPlayersName } from "../lib/coachPlayersName";
import LearnChrome, { useLearnNewLook, useLearnSectionsLook } from "../components/LearnChrome";
import AssignedDrillsRows from "../components/AssignedDrillsRows";
import { MoreSection, SectionStrip, SectionPanel } from "../components/kit";
import { LEARN_TOPIC_KEYS, learnSearch, learnTopicHref, readLearnLocation } from "../lib/learnSection";
import { readLearnTopic, writeLearnTopic } from "../lib/moreMemory";

import LearningHubLegacy, { TOPICS, GUIDE_GROUPS, slugify, fmtAssignedWhen } from "./LearningHubLegacy";
const NAVY = "#0B2E59", GOLD = "#D4A017";

// Issue #1128 (D221 O2): the ten tabs became two sections, My training and
// Guides, with the eight topics a list inside Guides. A bare `/learn` still
// lands on My training (#218: the thing you DO goes ahead of the things you
// read), and every old `?tab=` link still resolves (`lib/learnSection.js`
// holds the mapping and the reasons).
// Read by the responsive harness (`e2e/tabs.js`, the #405 nested-axis mechanism):
// under the Guides section the URL carries a second param, `topic`, with these
// eight values, so each topic view is visited and curated (`/learn|guides|<topic>`).
export const SECTION_AXES = { guides: { param: "topic", values: LEARN_TOPIC_KEYS } };

const SECTION_ITEMS = [
  { key: "training", label: "My training", testId: "learn-section-training" },
  { key: "guides", label: "Guides", testId: "learn-section-guides" },
];
// #1159 (CoachTrainingSessions board, round 12, D298): a coach has three
// sections. The `training` key stays the Sessions section, so `?tab=training`,
// the notification links and the journeys keep landing there.
const COACH_SECTION_ITEMS = [
  { key: "training", label: "Sessions", testId: "learn-section-training" },
  { key: "drills", label: "Drill bank", testId: "learn-section-drills" },
  { key: "guides", label: "Guides", testId: "learn-section-guides" },
];


// ---------------------------------------------------------------------------
// The page's pieces (#1128). Boards: TrainingHome.dc.html and
// TrainingGuides.dc.html, round 10 (D243). Rows, the heading block and the
// search are the boards' own px values through the kit tokens.
// ---------------------------------------------------------------------------

const STACK = { display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)" };
const GROUP = { display: "flex", flexDirection: "column", gap: "var(--nw-space-grid)" };

/** The board's two-line heading block: the page eyebrow, then the section's name (and, for a coach's Sessions, a lede). */
function PageHeading({ title, lede = null }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-xtight)" }}>
      <div className="nw-eyebrow">Training &amp; guides</div>
      <h2 className="nw-title" style={{ margin: 0 }} data-testid="learn-page-title">{title}</h2>
      {lede && <p className="nw-lede" style={{ margin: 0 }} data-testid="learn-page-lede">{lede}</p>}
    </div>
  );
}

const COACH_SESSIONS_LEDE = "Schedule a session for a player. They add how it went once it's done.";
const COACH_DRILLS_LEDE = "Write a drill once, in your own words, and assign it to one player or several.";

/** The section's name: a player's "My training" is a coach's "Sessions" (#1159). */
function pageTitle(sectionKey, isCoach) {
  if (sectionKey === "guides") return "Guides";
  if (sectionKey === "drills") return "Drill bank";
  return isCoach ? "Sessions" : "My training";
}

/** The old hero banner, kept for the screens that are not on the new look yet (a coach, an admin, the flag off). */
function Hero({ isCoach }) {
  return (
    <div className="relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white" style={{ background: "linear-gradient(135deg, #0B2E59 0%, #15417A 100%)" }}>
      <div className="absolute inset-0 bg-grid-slate opacity-20" />
      <div className="relative flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#D4A017" }}>Cricket Coaching Hub</p>
          {/* #586 + #591: one role-aware hero, resolved in ONE place so the
              player copy (#586) and coach copy (#591) coexist instead of
              overwriting each other. Player AND admin read the player
              surface; only a coach reads the coach surface. */}
          <h1 className="font-display font-black text-3xl sm:text-4xl mt-1">Record a Training Session and Coaching fundamentals</h1>
          {isCoach ? (
            // #591: names the coach's tasks and points at where assignment
            // lives. Assignment is My Players, not here (#509).
            <p className="text-white/70 mt-2 max-w-2xl">{`Record training sessions, write drills into your bank, and read the coaching guides for batting, bowling, fielding, wicketkeeping, field placement, fitness, nutrition and mindset. To assign guides or drills to a player, go to ${coachPlayersName()}.`}</p>
          ) : (
            // #586 (Martin): recording a session first, not "quick reference
            // guides" only.
            <p className="text-white/70 mt-2 max-w-2xl">Log your training sessions, and read the coaching guides for batting, bowling, fielding, wicketkeeping, field placement, fitness, nutrition and mindset.</p>
          )}
        </div>
        <BookOpen size={80} className="text-white/10 hidden md:block" />
      </div>
    </div>
  );
}

const ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  minHeight: 56,
  boxSizing: "border-box",
  padding: "8px 14px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
  textDecoration: "none",
};

/** A white card-radius row that is a link: a title, a muted second line, a chevron. 56px tall, the boards' row. */
function RowLink({ to, state, title, sub, testId, subTestId }) {
  return (
    <Link to={to} state={state} data-testid={testId} style={ROW_STYLE}>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span className="nw-rowname">{title}</span>
        {sub ? (
          <span className="nw-help" data-testid={subTestId}>
            {sub}
          </span>
        ) : null}
      </span>
      <ChevronRight size={20} style={{ color: "var(--nw-ink-faint)", flexShrink: 0 }} aria-hidden="true" />
    </Link>
  );
}

function GroupLabel({ children, testId }) {
  return (
    <div className="nw-section" style={{ fontSize: 11 }} data-testid={testId}>
      {children}
    </div>
  );
}

/** One topic as a row on the Guides list. */
function TopicRow({ topicKey, testId, withTagline = false }) {
  const topic = TOPICS[topicKey];
  // TrainingGuides board: only the face row, "Your last topic", carries the
  // tagline; the eight rows behind All eight topics are the name alone.
  return <RowLink to={learnTopicHref(topicKey)} title={topic.label} sub={withTagline ? topic.tagline : null} testId={testId} />;
}

/** "My training" (a player) or "Sessions" (a coach): the session list alone. */
function TrainingSection() {
  return (
    <div style={STACK} data-testid="learn-training">
      {/* The player's tip lines are gone (#1128, the boards draw none), and so
          is the coach's prompt (#1159: the board drops it with the hero). */}
      <TrainingSessions newLook />
    </div>
  );
}

/** The coach's "Drill bank" section (#1159): the bank alone. An admin is on the legacy page. */
function DrillsSection() {
  return (
    <div style={STACK} data-testid="learn-drills">
      <CoachDrillBankSection />
    </div>
  );
}

/** Assigned to you (player only): the guides a coach asked for, then the drills a coach wrote (#382, #461, #517). */
function AssignedBlock({ assigned, assignedStatus, onRetryAssigned, assignedDrills, drillsStatus, onRetryDrills, blockRef }) {
  const bothLoaded = assignedStatus === "loaded" && drillsStatus === "loaded";
  return (
    <div ref={blockRef} style={GROUP} data-testid="assigned-panel">
      <GroupLabel testId="guides-assigned">Assigned to you</GroupLabel>
      {/* #517: a failed fetch is stated as a failure with a retry, never
          rendered as the empty claim below. One note per loader. */}
      {assignedStatus === "failed" && (
        <LoadFailureNote what="your assigned guides" testId="assigned-guides-error" onRetry={onRetryAssigned} />
      )}
      {drillsStatus === "failed" && (
        <LoadFailureNote what="your assigned drills" testId="assigned-drills-error" onRetry={onRetryDrills} />
      )}
      {/* "Nothing assigned yet" is a claim about BOTH lists, so it may only
          render once both have actually come back (#517). */}
      {bothLoaded && assigned.length === 0 && assignedDrills.length === 0 && (
        <p className="nw-help" data-testid="assigned-empty" style={{ margin: 0 }}>
          Nothing assigned yet. Anything a coach asks you to read or work on will appear here.
        </p>
      )}
      {assigned.map((a) => (
        <RowLink
          key={a.assignment_id}
          to={`/learn/${a.category}/${a.slug}`}
          state={{ from: "/learn?section=guides" }}
          testId={`assigned-guide-${a.category}-${a.slug}`}
          title={a.title}
          // The attribution: #382's acceptance criterion, and the reason this
          // is not just another guide link.
          sub={`Assigned by ${a.assigned_by_coach_name}${fmtAssignedWhen(a.assigned_at) ? ` · ${fmtAssignedWhen(a.assigned_at)}` : ""}`}
          subTestId={`assigned-by-${a.category}-${a.slug}`}
        />
      ))}
      {/* Issue #461: drills a coach wrote for this player, under the guides. */}
      <AssignedDrillsRows drills={assignedDrills} />
    </div>
  );
}

/**
 * The Guides search (#1128): filters the published guides by title and by key
 * theme, client-side, in the order of `GUIDE_GROUPS`. No API call; the list is
 * the one the page already fetched.
 */
function searchGuides(publishedGuides, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const hit = (g) => `${g.title || ""}\n${g.key_theme || ""}`.toLowerCase().includes(needle);
  return GUIDE_GROUPS.flatMap(({ category }) => publishedGuides.filter((g) => g.category === category && hit(g)));
}

function GuideSearchResults({ query, publishedGuides, guidesStatus, onRetryGuides }) {
  if (guidesStatus === "failed") {
    return <LoadFailureNote what="the coaching guides" testId="learn-guides-error" onRetry={onRetryGuides} />;
  }
  if (guidesStatus !== "loaded") return null;
  const results = searchGuides(publishedGuides, query);
  if (results.length === 0) {
    return (
      // The board's empty card (TrainingGuides, "A search with nothing in it"):
      // a centred white card with the 52px icon tile, the line, then the hint.
      <div
        data-testid="learn-guide-search-empty"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          padding: "28px 20px",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-card)",
          textAlign: "center",
        }}
      >
        <div
          aria-hidden="true"
          style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nw-navy-tint)", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Search size={22} style={{ color: "var(--nw-navy)" }} />
        </div>
        <div className="nw-body-strong">{`No guide matches ${query.trim()}.`}</div>
        <div className="nw-lede">Try a shorter word.</div>
      </div>
    );
  }
  return (
    <div style={GROUP} data-testid="learn-guide-results">
      {results.map((g) => (
        <RowLink
          key={`${g.category}/${g.slug}`}
          to={`/learn/${g.category}/${g.slug}`}
          state={{ from: "/learn?section=guides" }}
          testId={`guide-link-${g.category}-${g.slug}`}
          title={g.title}
          sub={g.key_theme}
        />
      ))}
    </div>
  );
}

/** Guides, the face (TrainingGuides board): search, Assigned to you, your last topic, All eight topics. */
function GuidesFace({ isPlayer, lastTopic, assignedProps, guideProps }) {
  const [query, setQuery] = useState("");
  const searching = query.trim() !== "";
  const faceTopic = lastTopic || LEARN_TOPIC_KEYS[0];
  return (
    <div style={STACK} data-testid="learn-guides">
      <label
        className="focus-within:ring-2 focus-within:ring-slate-400"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minHeight: 46,
          padding: "0 12px",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-control)",
        }}
      >
        <Search size={18} style={{ color: "var(--nw-ink-faint)", flexShrink: 0 }} aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the guides"
          aria-label="Search the guides"
          data-testid="learn-guide-search"
          className="nw-body"
          style={{ flex: 1, minWidth: 0, minHeight: 44, border: 0, outline: "none", background: "transparent" }}
        />
      </label>
      {searching ? (
        <GuideSearchResults query={query} {...guideProps} />
      ) : (
        <>
          {isPlayer && <AssignedBlock {...assignedProps} />}
          <div style={GROUP}>
            <GroupLabel testId="guides-recent">{lastTopic ? "Your last topic" : "Topics"}</GroupLabel>
            <TopicRow topicKey={faceTopic} testId="learn-topic-last" withTagline />
          </div>
          <MoreSection screen="learn-topics" label="All eight topics" lessLabel="Fewer topics" data-testid="learn-topics-more">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-tight)" }}>
              {LEARN_TOPIC_KEYS.map((key) => (
                <TopicRow key={key} topicKey={key} testId={`learn-topic-${key}`} />
              ))}
            </div>
          </MoreSection>
        </>
      )}
    </div>
  );
}

/**
 * One concept card of a topic: the hand-written level-1 guide, a disclosure
 * that opens in place (#96, #458). Drawn with kit tokens, not the shadcn Card
 * (#1128: the legacy-free guard, journey 65, counts `Card`).
 */
function ConceptCard({ topicKey, topic, card, openTopic, setOpenTopic }) {
  const openKey = `${topicKey}:${slugify(card.title)}`;
  const isOpen = openTopic === openKey;
  const triggerId = `qrg-trigger-${openKey}`;
  const panelId = `qrg-panel-${openKey}`;
  return (
    <div
      data-state={isOpen ? "open" : "closed"}
      // Issue #458 (Martin, 2026-08-23): an OPEN guide spans the whole row
      // from `md:` up, so a player reads it at a usable width instead of a
      // third of the screen. Order is preserved; only one card is open.
      className={cn("overflow-hidden", isOpen && "md:col-span-3")}
      style={{
        background: isOpen ? `${topic.color}05` : "var(--nw-surface)",
        border: isOpen ? `2px solid ${topic.color}` : "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      {/* A real <button>, so Tab/Enter/Space and the focus ring come for free.
          The inset ring survives the card's overflow clip. */}
      <button
        type="button"
        id={triggerId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setOpenTopic(isOpen ? null : openKey)}
        data-testid={`topic-${card.title}`}
        className="w-full text-left bg-transparent border-0 p-5 cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="nw-rowname">{card.title}</div>
          <ChevronDown
            size={16}
            className={cn("mt-1 text-slate-400 transition-transform flex-shrink-0", isOpen && "rotate-180")}
            aria-hidden="true"
          />
        </div>
        <div className="nw-help" style={{ marginTop: 2 }}>{card.purpose}</div>
      </button>

      {isOpen && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          data-testid="qrg-content"
          // Two columns from `lg:` up (#458): purpose beside the points keeps
          // both at a readable measure.
          className="px-5 pb-5 pt-1 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start border-t border-slate-100"
        >
          <div className="rounded-xl p-4 border-l-4" style={{ background: `${topic.color}0A`, borderColor: topic.color }}>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: topic.color }}>Purpose</div>
            <p className="text-sm text-slate-800">{card.purpose}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ListChecks size={14} color={topic.color} />
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Key coaching points</div>
            </div>
            <ul className="space-y-1.5">
              {card.points.map((p, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: topic.color }} />{p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/** Guides, one topic: what used to be that topic's tab (concept cards, then the published guides by group). */
function TopicView({ topicKey, openTopic, setOpenTopic, publishedGuides, guidesStatus, onRetryGuides }) {
  const topic = TOPICS[topicKey];
  return (
    <div style={STACK} data-testid="learn-topic-view" data-topic={topicKey}>
      <Link
        to="/learn?section=guides"
        data-testid="learn-topic-back"
        className="nw-body-strong"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44, alignSelf: "flex-start", color: "var(--nw-navy)", textDecoration: "none" }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> All topics
      </Link>
      <PageHeading title={topic.label} />
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Sparkles size={14} color={topic.color} /> {topic.tagline}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start" data-testid={`grid-${topicKey}`}>
        {topic.cards.map((card) => (
          <ConceptCard key={card.title} topicKey={topicKey} topic={topic} card={card} openTopic={openTopic} setOpenTopic={setOpenTopic} />
        ))}
      </div>

      {/* #517: a failed guide-list fetch is stated as a failure with a retry,
          never the silent "no guides here" the groups below collapse to. */}
      {guidesStatus === "failed" && (
        <LoadFailureNote what="the coaching guides" testId="learn-guides-error" onRetry={onRetryGuides} />
      )}

      {/* Level 2 (issues #219/#220, widened by #390): the imported guides,
          menu-only per spec §2.1 point 2: title + key_theme subtitle. One
          group per category the API placed on this topic's surface; an empty
          group renders nothing. Nothing here can show an unreviewed guide:
          `fetchPublishedGuides` reads the gated endpoint. */}
      {GUIDE_GROUPS.map(({ category, label }) => {
        const groupGuides = publishedGuides.filter((g) => g.surface === topicKey && g.category === category);
        if (groupGuides.length === 0) return null;
        return (
          <div key={category} data-testid={`guide-group-${category}`}>
            {/* #503 defect 2: the group header is bolder and darker with a
                hairline rule so every group reads as a section break. */}
            <div className="flex items-center gap-2 mb-2.5 mt-2">
              <div className="text-[13px] uppercase tracking-widest font-bold text-slate-700 whitespace-nowrap">{label}</div>
              <div className="h-px flex-1 bg-slate-200" aria-hidden="true" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-tight)" }}>
              {groupGuides.map((g) => (
                // Category-qualified key and testid, not slug alone: the corpus
                // has `leg-spin` and `off-spin` in BOTH bowling and
                // facing-bowling, so a slug-only key is a real collision.
                <RowLink
                  key={`${g.category}/${g.slug}`}
                  to={`/learn/${g.category}/${g.slug}`}
                  // #1125: the guide's Back returns to THIS topic.
                  state={{ from: learnTopicHref(topicKey) }}
                  testId={`guide-link-${g.category}-${g.slug}`}
                  title={g.title}
                  sub={g.key_theme}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const validTopic = (v) => (LEARN_TOPIC_KEYS.includes(v) ? v : null);

function LearnSectionsPage() {
  const [sp, setSp] = useSearchParams();
  const [openTopic, setOpenTopic] = useState(null);
  // Issue #480 — Assigned to you is player-only. Role, not "has a player
  // profile": the endpoint's `resolveScope` (api/src/routes/authz.ts)
  // branches on role before it looks for a profile, so a coach who also holds
  // a player profile still gets nothing from it.
  const { user } = useAuth();
  const isPlayer = user?.role === "player";
  // Issue #1120: a player on the new look gets the kit bar and the ribbon
  // (LearnChrome); the strip leads the page and the hero is dropped.
  const newLook = useLearnNewLook();
  // Issue #591 — COACH-only: an admin reads the player-side surface on this
  // page, so only a coach gets the coach hero and the coach prompt.
  const isCoach = user?.role === "coach";
  // Issue #1128: two sections. `learnLoc` reads `?section=`, the old `?tab=`
  // and `?topic=`; `chosen` is what the person ASKED for, `null` on a bare
  // `/learn` (#464).
  // The responsive harness enumerates this page's sections from source
  // (`e2e/tabs.js` follows `currentKey={section}` back to `sp.get("section")`),
  // so `section` stays a plain `const` read from `learnLoc`, and no other
  // function in this file declares a `section` of its own.
  const rawLoc = readLearnLocation({ section: sp.get("section"), tab: sp.get("tab"), topic: sp.get("topic") });
  // #1159: the Drill bank is a coach's section; a player who lands on
  // `?section=drills` is read as a bare `/learn` (My training).
  const learnLoc = rawLoc.section === "drills" && !isCoach ? readLearnLocation({}) : rawLoc;
  const section = learnLoc.section;

  /**
   * Issue #417: landing on My training is "going to it on the platform" for a
   * coach-scheduled session (`coach_session`); landing on the Guides face is
   * seeing what a coach assigned (`coach_guide`, `coach_drill`: the old
   * Assigned tab's surface). Fires on every transition INTO a section, not on
   * every render.
   *
   * ISSUE #464 — `chosen`, not the defaulted section: a bare `/learn` lands on
   * My training by default and clears nothing; an explicit ask does.
   */
  useEffect(() => {
    markSurfaceNotificationsRead("learn", learnLoc.chosen);
  }, [learnLoc.chosen]);

  // Issues #219/#220 (epic #315), widened by #390: every reference tab is TWO
  // levels now — the three hand-written cards stay level 1 unchanged (PR #191,
  // Martin: "do not normalise the three concept cards into the guide schema —
  // there is no source document behind them to populate it"); the imported
  // guides are level 2, each rendered on its own page at
  // `/learn/:category/:slug` (GuidePage.jsx). Fetched once here, not per-tab.
  //
  // #219/#220 filtered to the batting category alone, because only batting
  // had been wired. #390's twenty-six technique guides (bowling 5,
  // facing-bowling 8, fielding 13) are already imported and reviewable, so
  // the filter moves to `surface` — the API's OWN answer to "which tab does
  // this guide belong on" (`CATEGORY_SURFACE` in `api/src/content/schema/guide.ts`:
  // facing bowling is a batting skill, field placement is captaincy the
  // fielding side does, mindset goes to Reflections). Deriving the tab here
  // from `category` instead would be a second copy of that mapping, free to
  // drift from the server's.
  //
  // #391 adds the Fitness tab (`surface: "fitness"`); #503 adds the Mindset
  // tab (`surface: "mindset"`, re-mapped off the never-built "reflections"
  // destination); #514 adds the Nutrition tab (`surface: "nutrition"`,
  // re-mapped off the fitness surface #391 folded it onto — fitness keeps
  // its own guides and drills). Grouping within a tab is by category, so
  // the eight facing-bowling guides read as their own labelled group under
  // Batting rather than being mixed into the twelve shot guides.
  //
  // Only PUBLISHED guides ever come back from this endpoint, so an unreviewed
  // guide is invisible here for every role — the same gate `learnGuides.ts`
  // enforces server-side, not a frontend filter.
  const [publishedGuides, setPublishedGuides] = useState([]);
  // Issue #517: this fetch used to swallow its failure into a permanently
  // empty guide list — every reference tab silently rendered as "no guides
  // here yet", a false claim of empty about a list that was never fetched
  // (the #271 class journeys 8d/8e forbid; #509/#511 fixed the same defect
  // in CoachPortal's picker, whose three-state idiom this mirrors). Three
  // states, because "empty" may only be claimed from "loaded".
  const [guidesStatus, setGuidesStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [guidesRetryNonce, setGuidesRetryNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setGuidesStatus("loading");
    fetchPublishedGuides()
      .then((guides) => {
        if (cancelled) return;
        setPublishedGuides(guides);
        setGuidesStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setGuidesStatus("failed");
      });
    return () => { cancelled = true; };
  }, [guidesRetryNonce]);

  /**
   * Issue #382 — the guides a coach has ASKED this player to read.
   *
   * A separate fetch from the published list above, and deliberately not a
   * filter over it: the assignment carries WHO asked and WHEN, which is the
   * whole point ("my coach asked me to read this" is a fact, not a guess —
   * the same attribution #190 gave a coach-set goal). Only published guides
   * ever come back, because the endpoint joins through the review rows; a
   * guide whose sign-off is later withdrawn drops out of this list on the
   * next load without the player being told a guide vanished.
   *
   * Issue #480 — the tab itself is PLAYER-ONLY (`isPlayer` above gates the
   * trigger): for a coach or admin this fetch would be a 4xx by
   * construction (`resolveScope` requires a `player_id` for those roles and
   * none is sent here), so the tab could never be anything but empty on
   * their screen, and Martin asked for it to go. Only the TRIGGER is gated
   * — a coach who deep-links `/learn?tab=assigned` still gets the harmless
   * empty panel (the pre-#480 status quo) rather than a blank page. A
   * coach's real view of assignments is Coach Portal → player → Guides.
   *
   * Issue #517 — a FAILED fetch no longer collapses into "Nothing assigned
   * yet": that is a claim about the player's data, and making it about a
   * list this page failed to fetch is the #271 false-empty class journeys
   * 8d/8e forbid. Three states, same idiom as CoachPortal's catalogue
   * fetch (#511): the empty card may only render from "loaded", and
   * "failed" renders a stated failure with a retry.
   *
   * The ROLE case and the network case are told apart BEFORE the request,
   * not by inspecting the error after it: for a non-player the 4xx is by
   * construction (above) — the client already knows the endpoint has
   * nothing for this role, exactly the reasoning that gates the trigger —
   * so no request is made and the state goes straight to "loaded" with the
   * empty list, keeping #480's deliberate quiet-empty panel. "failed" is
   * therefore only ever reachable for a role the endpoint actually serves,
   * which makes it mean what the note says: the network or server failed.
   */
  const [assigned, setAssigned] = useState([]);
  const [assignedStatus, setAssignedStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [assignedRetryNonce, setAssignedRetryNonce] = useState(0);
  useEffect(() => {
    if (!isPlayer) {
      setAssignedStatus("loaded");
      return undefined;
    }
    let cancelled = false;
    setAssignedStatus("loading");
    fetchAssignedGuides()
      .then((rows) => {
        if (cancelled) return;
        setAssigned(rows);
        setAssignedStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setAssignedStatus("failed");
      });
    return () => { cancelled = true; };
  }, [isPlayer, assignedRetryNonce]);

  /**
   * Issue #461 — the drills a coach has WRITTEN for this player, shown on
   * the same Assigned tab under the guides. Same posture as the guide
   * fetch above in every particular (#517 included): separate fetch (a
   * drill is a `coach_drills` row, not bundle content), the role case is
   * decided before the request while a real failure is stated with a
   * retry, and #480's player-only TRIGGER gating covers this list for
   * free because it lives inside the same panel.
   */
  const [assignedDrills, setAssignedDrills] = useState([]);
  const [drillsStatus, setDrillsStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [drillsRetryNonce, setDrillsRetryNonce] = useState(0);
  useEffect(() => {
    if (!isPlayer) {
      setDrillsStatus("loaded");
      return undefined;
    }
    let cancelled = false;
    setDrillsStatus("loading");
    fetchAssignedDrills()
      .then((rows) => {
        if (cancelled) return;
        setAssignedDrills(rows);
        setDrillsStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setDrillsStatus("failed");
      });
    return () => { cancelled = true; };
  }, [isPlayer, drillsRetryNonce]);

  // Issue #1128: "Your last topic" is the last topic this person opened, kept
  // per person (`lib/moreMemory.js`, `nw_learn_topic:<userId>`). Opening a
  // topic (a `?topic=` in the URL, including through an old `?tab=<topic>`)
  // is what writes it.
  const userId = user?.id;
  const [lastTopic, setLastTopic] = useState(() => validTopic(readLearnTopic(userId)));
  useEffect(() => {
    setLastTopic(validTopic(readLearnTopic(userId)));
  }, [userId]);
  useEffect(() => {
    if (learnLoc.topic && userId !== undefined && userId !== null) {
      writeLearnTopic(userId, learnLoc.topic);
      setLastTopic(learnLoc.topic);
    }
  }, [learnLoc.topic, userId]);

  // The old `?tab=assigned` link: the assigned list is on the Guides face now,
  // so bring it into view once both lists have come back.
  const assignedRef = useRef(null);
  const assignedReady = assignedStatus === "loaded" && drillsStatus === "loaded";
  useEffect(() => {
    const el = assignedRef.current;
    if (learnLoc.assigned && isPlayer && assignedReady && el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "start" });
    }
  }, [learnLoc.assigned, isPlayer, assignedReady]);

  const chooseSection = (key) => {
    setSp(learnSearch(sp, { section: key }), { replace: true });
    setOpenTopic(null);
  };
  const strip = (
    <SectionStrip data-testid="learn-sections" idPrefix="learn-sections" items={isCoach ? COACH_SECTION_ITEMS : SECTION_ITEMS} currentKey={section} onChange={chooseSection} />
  );
  const onRetryGuides = () => setGuidesRetryNonce((n) => n + 1);

  let body;
  if (section === "training") {
    body = <TrainingSection />;
  } else if (section === "drills") {
    body = <DrillsSection />;
  } else if (learnLoc.topic) {
    body = (
      <TopicView
        topicKey={learnLoc.topic}
        openTopic={openTopic}
        setOpenTopic={setOpenTopic}
        publishedGuides={publishedGuides}
        guidesStatus={guidesStatus}
        onRetryGuides={onRetryGuides}
      />
    );
  } else {
    body = (
      <GuidesFace
        isPlayer={isPlayer}
        lastTopic={lastTopic}
        assignedProps={{
          assigned,
          assignedStatus,
          onRetryAssigned: () => setAssignedRetryNonce((n) => n + 1),
          assignedDrills,
          drillsStatus,
          onRetryDrills: () => setDrillsRetryNonce((n) => n + 1),
          blockRef: assignedRef,
        }}
        guideProps={{ publishedGuides, guidesStatus, onRetryGuides }}
      />
    );
  }

  return (
    <LearnChrome title="Training & guides">
      <div style={STACK} data-testid="learning-hub">
        {/* Q2 (D225 item 2): under the ribbon, above the heading. With no
            ribbon (admin, or the flag off) the old hero banner leads and the
            strip sits under it. */}
        {strip}
        {!newLook && !isCoach && <Hero isCoach={isCoach} />}
        {(newLook || isCoach) && !learnLoc.topic && (
          <PageHeading title={pageTitle(section, isCoach)} lede={isCoach ? { training: COACH_SESSIONS_LEDE, drills: COACH_DRILLS_LEDE }[section] ?? null : null} />
        )}
        <SectionPanel idPrefix="learn-sections" currentKey={section} style={STACK}>
          {body}
        </SectionPanel>
      </div>
    </LearnChrome>
  );
}

/**
 * D217: the old look stays until Martin has finished comparing. `/learn` has
 * two lives, like Settings (#1135) and Record (#1129): the two-section page
 * below for a player or a coach on the new look, and the shipped ten-tab page
 * (`LearningHubLegacy.jsx`, the file as it was on main, nothing else changed)
 * for everyone else: the flag off, and an admin.
 */
export default function LearningHub() {
  const sections = useLearnSectionsLook();
  return sections ? <LearnSectionsPage /> : <LearningHubLegacy />;
}
